import getReportSubmissionPolicy from './getReportSubmissionPolicy';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { type MailboxInfo, getIndexerValueForMailboxInfo } from 'owa-client-types';
import { isServiceRequestSupportedForMailbox } from 'owa-service/lib/utils/isServiceRequestSupportedForMailbox';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { logUsage } from 'owa-analytics';

/**
 * Reason describing the outcome of the native reporting enablement check.
 * - `enabled`: native reporting actions are available for the mailbox
 * - `disabledByFlag`: disabled globally via the `tri-disable-reporting` kill switch
 * - `serviceRequestNotSupported`: service requests aren't supported for the mailbox
 * - `policyRestricted`: enterprise report submission policy doesn't allow native reporting
 */
type NativeReportingReason =
    | 'enabled'
    | 'disabledByFlag'
    | 'serviceRequestNotSupported'
    | 'policyRestricted';

// Tracks mailboxes we've already logged the native reporting state for, so we
// only emit the telemetry once per session per mailbox (the selector is called
// frequently from menu/ribbon gating on every render).
const loggedMailboxes = new Set<string>();

function getNativeReportingState(mailboxInfo: MailboxInfo): {
    isEnabled: boolean;
    reason: NativeReportingReason;
    customData: Record<string, boolean>;
} {
    // https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1768548
    if (isGlobalFeatureEnabled('tri-disable-reporting')) {
        return { isEnabled: false, reason: 'disabledByFlag', customData: {} };
    }

    const isServiceRequestAllowed = isServiceRequestSupportedForMailbox(mailboxInfo);
    const consumer = isConsumer(undefined, mailboxInfo);

    // Check Report Submission Policy values for enterprise users
    if (isServiceRequestAllowed && !consumer) {
        const reportSubmissionPolicy = getReportSubmissionPolicy(mailboxInfo);

        const isEnabled =
            (reportSubmissionPolicy.EnableReportToMicrosoft &&
                !reportSubmissionPolicy.EnableThirdPartyAddress) ||
            (reportSubmissionPolicy.ReportJunkToCustomizedAddress &&
                reportSubmissionPolicy.ReportPhishToCustomizedAddress &&
                reportSubmissionPolicy.ReportNotJunkToCustomizedAddress);

        return {
            isEnabled,
            reason: isEnabled ? 'enabled' : 'policyRestricted',
            customData: {
                enableReportToMicrosoft: reportSubmissionPolicy.EnableReportToMicrosoft,
                enableThirdPartyAddress: reportSubmissionPolicy.EnableThirdPartyAddress,
                reportJunkToCustomizedAddress: reportSubmissionPolicy.ReportJunkToCustomizedAddress,
                reportPhishToCustomizedAddress:
                    reportSubmissionPolicy.ReportPhishToCustomizedAddress,
                reportNotJunkToCustomizedAddress:
                    reportSubmissionPolicy.ReportNotJunkToCustomizedAddress,
            },
        };
    }

    // Consumers with service requests allowed default to enabled; otherwise the
    // check is off because service requests aren't supported for the mailbox.
    return {
        isEnabled: isServiceRequestAllowed,
        reason: isServiceRequestAllowed ? 'enabled' : 'serviceRequestNotSupported',
        customData: {},
    };
}

/**
 * Checks if native reporting actions (Junk/Phishing) are enabled in the tenant
 *
 * @param mailboxInfo mailboxInfo of the mailbox that the user is reporting from
 * @returns boolean representing whether we should show native reporting actions (Junk/Phishing)
 */
export default function isNativeReportingEnabled(mailboxInfo: MailboxInfo): boolean {
    const { isEnabled, reason, customData } = getNativeReportingState(mailboxInfo);

    const indexer = getIndexerValueForMailboxInfo(mailboxInfo);
    if (!loggedMailboxes.has(indexer)) {
        loggedMailboxes.add(indexer);
        logUsage('Reporting_NativeReportingState', mailboxInfo, {
            isEnabled,
            reason,
            ...customData,
        });
    }

    return isEnabled;
}
